import React from 'react'
import * as CheckboxPrimitive from "@radix-ui/react-checkbox"
import { Checkbox } from '../../components/checkbox'

// Base UICheckbox - re-export shadcn Checkbox for other composed components
export const UICheckboxBase = Checkbox
import { UILabel } from './ui-label'
import { UITypography } from '../display/ui-typography'
import { UIBox } from '../layout/ui-box'
import { cn } from '../../lib/utils'
import { CheckIcon, Heart, Star, BookmarkIcon, Pizza, Sandwich, Beer, IceCreamBowl } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { UICheckboxCard } from './ui-checkbox-card'

export interface UICheckboxProps extends Omit<React.ComponentProps<typeof Checkbox>, 'checked'> {
  /**
   * 체크박스 ID (선택사항, 미제공시 자동 생성)
   */
  id?: string
  /**
   * 라벨
   */
  label?: string
  /**
   * 설명 텍스트
   */
  description?: string
  /**
   * 오류 메시지
   */
  error?: string
  /**
   * 체크박스 크기
   */
  size?: 'sm' | 'default' | 'lg'
  /**
   * 체크박스 변형
   */
  variant?: 'default' | 'icon' | 'indeterminate'
  /**
   * 체크 상태
   */
  checked?: boolean | 'indeterminate'
  /**
   * 체크 상태 변경 핸들러
   */
  onCheckedChange?: (checked: boolean | 'indeterminate') => void
  /**
   * 아이콘 (icon variant용)
   */
  icon?: LucideIcon
  /**
   * 체크된 상태 아이콘 (icon variant용) - filled 버전으로 사용
   */
  checkedIcon?: LucideIcon
  /**
   * 아이콘 색상 테마 (icon variant용)
   */
  colorTheme?: 'heart' | 'star' | 'bookmark' | 'primary' | 'accent'
  /**
   * 컨테이너 클래스명
   */
  className?: string
}

const sizeVariants = {
  sm: {
    checkbox: "size-3",
    icon: "size-3",
    checkIcon: "size-2.5", // 체크박스 내부 체크마크 크기
    label: "text-xs font-medium leading-none",
    description: "text-xs text-muted-foreground",
    spacing: "gap-1.5"
  },
  default: {
    checkbox: "size-4",
    icon: "size-4",
    checkIcon: "size-3.5", // 기본 크기 유지
    label: "text-sm font-medium leading-none",
    description: "text-xs text-muted-foreground",
    spacing: "gap-2.5"
  },
  lg: {
    checkbox: "size-5",
    icon: "size-5",
    checkIcon: "size-4", // 더 큰 체크마크
    label: "text-base font-medium leading-none",
    description: "text-sm text-muted-foreground",
    spacing: "gap-3"
  }
}

const iconThemes = {
  heart: {
    unchecked: "text-muted-foreground hover:text-rose-400",
    checked: "text-rose-500 fill-rose-500"
  },
  star: {
    unchecked: "text-muted-foreground hover:text-yellow-400",
    checked: "text-yellow-500 fill-yellow-500"
  },
  bookmark: {
    unchecked: "text-muted-foreground hover:text-blue-400",
    checked: "text-blue-600 fill-blue-600"
  },
  primary: {
    unchecked: "text-muted-foreground hover:text-primary",
    checked: "text-primary fill-primary"
  },
  accent: {
    unchecked: "text-muted-foreground hover:text-accent",
    checked: "text-accent fill-accent"
  }
}

const defaultIcons = {
  heart: Heart,
  star: Star,
  bookmark: BookmarkIcon
}

/**
 * 간소화된 체크박스 컴포넌트
 * 기본, 아이콘, 중간상태 변형을 지원합니다.
 */
export const UICheckbox = React.forwardRef<
  any,
  UICheckboxProps
>(({
  id,
  label,
  description,
  error,
  size = "default",
  variant = "default",
  checked = false,
  onCheckedChange,
  icon,
  checkedIcon,
  colorTheme = "primary",
  className,
  ...props
}, ref) => {
  const generatedId = React.useId()
  const checkboxId = id || generatedId
  const variants = sizeVariants[size]

  const isIndeterminate = checked === 'indeterminate'
  const isChecked = checked === true

  // Determine icons
  const defaultIcon = colorTheme in defaultIcons ? defaultIcons[colorTheme as keyof typeof defaultIcons] : CheckIcon
  const IconComponent: LucideIcon = icon || defaultIcon
  const CheckedIcon: LucideIcon = checkedIcon || IconComponent

  // Handle checkbox changes
  const handleChange = (newChecked: boolean | 'indeterminate') => {
    if (variant === 'indeterminate') {
      if (isIndeterminate) {
        onCheckedChange?.(true)
      } else if (isChecked) {
        onCheckedChange?.(false)
      } else {
        onCheckedChange?.(true)
      }
    } else {
      onCheckedChange?.(newChecked)
    }
  }

  // Icon variant - Radix UI + shadcn-ui-blocks 패턴 적용
  if (variant === 'icon' && IconComponent) {

    // 색상 테마별 아이콘 스타일 (filled 효과용)
    const getIconStyles = (isChecked: boolean, theme: string) => {
      if (!isChecked) {
        return theme === 'heart' ? 'text-muted-foreground hover:text-rose-400' :
               theme === 'star' ? 'text-muted-foreground hover:text-yellow-400' :
               theme === 'bookmark' ? 'text-muted-foreground hover:text-blue-400' :
               theme === 'primary' ? 'text-muted-foreground hover:text-primary' :
               'text-muted-foreground hover:text-accent'
      }

      return theme === 'heart' ? 'text-rose-500 fill-rose-500' :
             theme === 'star' ? 'text-yellow-400 fill-yellow-400' :
             theme === 'bookmark' ? 'text-blue-500 fill-blue-500' :
             theme === 'primary' ? 'text-primary fill-primary' :
             'text-accent fill-accent'
    }

    const uncheckedIconStyles = getIconStyles(false, colorTheme || 'accent')
    const checkedIconStyles = getIconStyles(true, colorTheme || 'accent')

    // aria-label 계산 (robust fallback)
    const getAriaUILabel = () => {
      if (label) return label

      const iconName = IconComponent.displayName || IconComponent.name ||
                      (colorTheme === 'heart' ? 'heart' :
                       colorTheme === 'star' ? 'star' :
                       colorTheme === 'bookmark' ? 'bookmark' : 'option')

      return `Toggle ${iconName}`
    }

    // aria-label 값 생성 및 검증
    const ariaUILabelValue = getAriaUILabel()

    // Icon variant requires CheckboxPrimitive.Root directly because it renders
    // custom icon children instead of the base Checkbox's built-in CheckIcon indicator.
    const checkboxElement = (
      <CheckboxPrimitive.Root
        ref={ref}
        id={checkboxId}
        className={cn(
          "peer group inline-flex items-center justify-center rounded-sm transition-shadow duration-200",
          "cursor-pointer",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1",
          variants.checkbox,
          props.disabled && "opacity-50 cursor-not-allowed"
        )}
        checked={isChecked}
        disabled={props.disabled}
        onCheckedChange={handleChange}
        aria-label={ariaUILabelValue}
      >
        {/* Unchecked state icon - 일반 outline 아이콘 */}
        <UIBox className={cn(
          "group-data-[state=checked]:hidden transition-colors duration-200",
          uncheckedIconStyles
        )}>
          <IconComponent className={variants.icon} />
        </UIBox>

        {/* Checked state icon - filled 버전 또는 동일 아이콘 + fill 스타일 */}
        <UIBox className={cn(
          "group-data-[state=unchecked]:hidden transition-colors duration-200",
          checkedIconStyles
        )}>
          {CheckedIcon ? (
            <CheckedIcon className={variants.icon} />
          ) : (
            <IconComponent className={variants.icon} />
          )}
        </UIBox>
      </CheckboxPrimitive.Root>
    )

    if (!label && !description && !error) {
      return checkboxElement
    }

    return (
      <UIBox className={cn("space-y-2", className)}>
        <UIBox className={cn(
          "flex",
          variants.spacing,
          description ? "items-start" : "items-center"
        )}>
          {checkboxElement}
          <UIBox className="space-y-1 flex-1">
            {label && (
              <UILabel
                htmlFor={checkboxId}
                className={cn(
                  "cursor-pointer font-medium",
                  variants.label,
                  error && "text-destructive",
                  isChecked && !error && "text-accent",
                  props.disabled && "opacity-50"
                )}
              >
                {label}
              </UILabel>
            )}
            {description && (
              <UITypography
                variant="caption"
                color="muted"
              >
                {description}
              </UITypography>
            )}
          </UIBox>
        </UIBox>
        {error && (
          <UITypography
            variant="caption"
            color="destructive"
            className="ml-7"
            role="alert"
          >
            {error}
          </UITypography>
        )}
      </UIBox>
    )
  }

  // Indeterminate variant
  if (variant === 'indeterminate') {
    // Filter out component-specific props before spreading to base Checkbox
    const {
      variant: _indVariant,
      icon: _indIcon,
      checkedIcon: _indCheckedIcon,
      colorTheme: _indColorTheme,
      label: _indLabel,
      description: _indDescription,
      error: _indError,
      size: _indSize,
      ...indeterminateCheckboxProps
    } = props as any

    const checkboxElement = (
      <Checkbox
        ref={ref}
        id={checkboxId}
        className={cn(
          variants.checkbox,
          isIndeterminate && "bg-primary border-primary",
          error && "border-destructive"
        )}
        checked={isChecked}
        onCheckedChange={handleChange}
        {...indeterminateCheckboxProps}
      />
    )

    if (!label && !description && !error) {
      return checkboxElement
    }

    return (
      <UIBox className={cn("space-y-2", className)}>
        <UIBox className={cn(
          "flex gap-3",
          description ? "items-start" : "items-center"
        )}>
          {checkboxElement}
          <UIBox className="space-y-1 flex-1">
            {label && (
              <UILabel htmlFor={checkboxId} className={cn(
                "cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
                variants.label,
                error && "text-destructive",
                (isChecked || isIndeterminate) && !error && "text-accent"
              )}>
                {label}
              </UILabel>
            )}
            {description && (
              <UITypography
                variant="caption"
                color="muted"
              >
                {description}
              </UITypography>
            )}
          </UIBox>
        </UIBox>
        {error && (
          <UITypography
            variant="caption"
            color="destructive"
            className="ml-7"
            role="alert"
          >
            {error}
          </UITypography>
        )}
      </UIBox>
    )
  }

  // Filter out component-specific props before spreading to base Checkbox
  const {
    variant: _variant,
    icon: _icon,
    checkedIcon: _checkedIcon,
    colorTheme: _colorTheme,
    label: _label,
    description: _description,
    error: _error,
    size: _size,
    ...checkboxProps
  } = props as any

  // Default variant - base Checkbox 사용
  const checkboxElement = (
    <Checkbox
      ref={ref}
      id={checkboxId}
      className={cn(
        variants.checkbox,
        error && "border-destructive"
      )}
      checked={isChecked}
      onCheckedChange={handleChange}
      {...checkboxProps}
    />
  )

  if (!label && !description && !error) {
    return checkboxElement
  }

  return (
    <UIBox className={cn("space-y-2", className)}>
      <UIBox className={cn(
        "flex gap-3",
        description ? "items-start" : "items-center"
      )}>
        {checkboxElement}
        <UIBox className="space-y-1 flex-1">
          {label && (
            <UILabel htmlFor={checkboxId} className={cn(
              "cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
              variants.label,
              error && "text-destructive",
              isChecked && !error && "text-accent"
            )}>
              {label}
            </UILabel>
          )}
          {description && (
            <UITypography variant="caption" color="muted">
              {description}
            </UITypography>
          )}
        </UIBox>
      </UIBox>
      {error && (
        <UITypography variant="p" size="sm" color="destructive" className="ml-7" role="alert">
          {error}
        </UITypography>
      )}
    </UIBox>
  )
})

// checkbox-03 패턴: Indeterminate Parent-Child 관계
export interface UICheckboxIndeterminateProps {
  /**
   * 자식 체크박스들의 초기 상태
   */
  defaultChildren?: Record<string, boolean>
  /**
   * 부모 라벨
   */
  parentLabel?: string
  /**
   * 자식 라벨들
   */
  childLabels?: Record<string, string>
  /**
   * 값 변경 핸들러
   */
  onValueChange?: (values: Record<string, boolean>) => void
  /**
   * 크기
   */
  size?: 'sm' | 'default' | 'lg'
  /**
   * 컨테이너 클래스명
   */
  className?: string
}

// checkbox-04/05 패턴: 간소화된 체크박스 그룹 컴포넌트
export interface UICheckboxGroupOption {
  value: string
  label: string
  description?: string
  icon?: LucideIcon
  disabled?: boolean
  defaultChecked?: boolean
}

export interface UICheckboxGroupProps {
  /**
   * 그룹 라벨
   */
  label?: string
  /**
   * 그룹 설명
   */
  description?: string
  /**
   * 오류 메시지
   */
  error?: string
  /**
   * 체크박스 옵션들
   */
  options: UICheckboxGroupOption[]
  /**
   * 선택된 값들
   */
  value?: string[]
  /**
   * 기본 선택된 값들
   */
  defaultValue?: string[]
  /**
   * 값 변경 핸들러
   */
  onValueChange?: (value: string[]) => void
  /**
   * 체크박스 크기
   */
  size?: 'sm' | 'default' | 'lg'
  /**
   * 레이아웃 모드 (checkbox-04: horizontal, checkbox-05: vertical)
   */
  layout?: 'vertical' | 'horizontal'
  /**
   * 스타일 변형
   */
  variant?: 'default' | 'card'
  /**
   * 컨테이너 클래스명
   */
  className?: string
}

export const UICheckboxGroup = React.forwardRef<
  HTMLDivElement,
  UICheckboxGroupProps
>(({
  label,
  description,
  error,
  options,
  value,
  defaultValue = [],
  onValueChange,
  size = "default",
  layout = "vertical",
  variant = "default",
  className,
  ...props
}, ref) => {
  // Controlled vs Uncontrolled 패턴 구현
  const isControlled = value !== undefined
  const [internalValue, setInternalValue] = React.useState<string[]>(defaultValue)

  // 실제 사용할 값 결정
  const currentValue = isControlled ? value : internalValue

  // Handle default checked options (uncontrolled 모드에서만)
  React.useEffect(() => {
    if (!isControlled) {
      const defaultCheckedValues = options
        .filter(option => option.defaultChecked && !internalValue.includes(option.value))
        .map(option => option.value)

      if (defaultCheckedValues.length > 0) {
        const newValue = [...internalValue, ...defaultCheckedValues]
        setInternalValue(newValue)
        onValueChange?.(newValue)
      }
    }
  }, [options, internalValue, onValueChange, isControlled])

  const handleCheckboxChange = (optionValue: string, checked: boolean) => {
    const newValue = checked
      ? [...currentValue, optionValue]
      : currentValue.filter(v => v !== optionValue)

    // Uncontrolled 모드에서만 내부 상태 업데이트
    if (!isControlled) {
      setInternalValue(newValue)
    }

    // 항상 외부로 변경 사항 알림
    onValueChange?.(newValue)
  }

  // Layout container classes
  const layoutClass = layout === 'horizontal'
    ? "flex items-center gap-4 flex-wrap"
    : "flex flex-col gap-4"

  return (
    <UIBox ref={ref} className={cn("space-y-4", className)} {...props}>
      {/* Group header */}
      {label && (
        <UILabel className={cn(
          "text-sm font-medium",
          error && "text-destructive"
        )}>
          {label}
        </UILabel>
      )}

      {description && (
        <UITypography variant="caption" color="muted">{description}</UITypography>
      )}

      {/* Checkbox group */}
      <UIBox className={layoutClass}>
        {options.map((option) => {
          const isChecked = currentValue.includes(option.value)
          const Icon = option.icon
          const checkboxId = `${option.value}-${layout}`

          // Card variant - 재사용 UICheckboxCard 컴포넌트
          if (variant === 'card') {
            return (
              <UICheckboxCard
                key={option.value}
                id={checkboxId}
                title={option.label}
                description={option.description}
                icon={Icon}
                variant="card"
                size={size}
                disabled={option.disabled}
                checked={isChecked}
                onCheckedChange={(checked) =>
                  handleCheckboxChange(option.value, checked)
                }
              />
            )
          }

          // Default variant
          return (
            <UICheckbox
              key={option.value}
              id={checkboxId}
              label={option.label}
              description={option.description}
              size={size}
              disabled={option.disabled}
              checked={isChecked}
              onCheckedChange={(checked) =>
                handleCheckboxChange(option.value, checked === true)
              }
              variant={Icon ? 'icon' : 'default'}
              icon={Icon}
              colorTheme="accent"
            />
          )
        })}
      </UIBox>

      {/* Error message */}
      {error && (
        <UITypography variant="caption" color="destructive" role="alert">
          {error}
        </UITypography>
      )}
    </UIBox>
  )
})

// checkbox-03 패턴: Indeterminate Parent-Child 컴포넌트
export const UICheckboxIndeterminate = React.forwardRef<
  HTMLDivElement,
  UICheckboxIndeterminateProps
>((
  {
    defaultChildren = { child1: true, child2: false },
    parentLabel = "Parent",
    childLabels = { child1: "Child 1", child2: "Child 2" },
    onValueChange,
    size = "default",
    className,
    ...props
  },
  ref
) => {
  const [checked, setChecked] = React.useState<Record<string, boolean>>(defaultChildren)

  // 고유 ID 생성 - 접근성을 위한 필수 요소
  const parentId = React.useId()
  const groupId = React.useId()

  const variants = sizeVariants[size]

  const handleCheckedChange = (name: string, newChecked: boolean | 'indeterminate') => {
    const updatedChecked = {
      ...checked,
      [name]: newChecked === true
    }
    setChecked(updatedChecked)
    onValueChange?.(updatedChecked)
  }

  const handleParentCheckedChange = (newChecked: boolean | 'indeterminate') => {
    const allChildrenValue = newChecked === true
    const updatedChecked: Record<string, boolean> = {}
    Object.keys(childLabels).forEach(key => {
      updatedChecked[key] = allChildrenValue
    })
    setChecked(updatedChecked)
    onValueChange?.(updatedChecked)
  }

  // 부모 체크박스 상태 계산 (shadcn-ui-blocks checkbox-03 패턴)
  const childValues = Object.values(checked)
  const allChecked = childValues.every(v => v === true)
  const noneChecked = childValues.every(v => v === false)
  const parentChecked = allChecked ? true : noneChecked ? false : 'indeterminate'

  return (
    <UIBox ref={ref} className={cn("space-y-4", className)} {...props}>
      {/* Parent checkbox */}
      <UIBox className="flex items-center gap-3">
        <UICheckbox
          id={parentId}
          checked={parentChecked}
          onCheckedChange={handleParentCheckedChange}
          variant="indeterminate"
          size={size}
        />
        <UILabel
          htmlFor={parentId}
          className={cn("font-medium cursor-pointer", variants.label)}
        >
          {parentLabel}
        </UILabel>
      </UIBox>

      {/* Child checkboxes */}
      <UIBox className="pl-6 space-y-3">
        {Object.entries(childLabels).map(([key, label]) => {
          const childId = `${groupId}-${key}`
          return (
            <UIBox key={key} className="flex items-center gap-3">
              <UICheckbox
                id={childId}
                checked={checked[key] === true}
                onCheckedChange={(newChecked) => handleCheckedChange(key, newChecked)}
                size={size}
              />
              <UILabel
                htmlFor={childId}
                className={cn("font-medium cursor-pointer", variants.label)}
              >
                {label}
              </UILabel>
            </UIBox>
          )
        })}
      </UIBox>
    </UIBox>
  )
})

// checkbox-04 패턴: Horizontal Checkbox Group (기술 스택 예시)
export const UICheckboxHorizontalGroup = React.forwardRef<
  HTMLDivElement,
  Omit<UICheckboxGroupProps, 'layout'>
>(({ options, ...props }, ref) => {
  const defaultTechOptions = [
    { value: 'react', label: 'React' },
    { value: 'next', label: 'Next' },
    { value: 'node', label: 'Node' },
    { value: 'remix', label: 'Remix' }
  ]

  return (
    <UICheckboxGroup
      ref={ref}
      options={options || defaultTechOptions}
      layout="horizontal"
      {...props}
    />
  )
})

// checkbox-05 패턴: Vertical Checkbox Group with Icons (음식 예시)
export const UICheckboxVerticalGroup = React.forwardRef<
  HTMLDivElement,
  Omit<UICheckboxGroupProps, 'layout'>
>(({ options, ...props }, ref) => {
  const defaultFoodOptions = [
    { value: 'pizza', label: 'Pizza', icon: Pizza, defaultChecked: true },
    { value: 'sandwich', label: 'Sandwich', icon: Sandwich, defaultChecked: true },
    { value: 'beer', label: 'Beer', icon: Beer },
    { value: 'ice-cream', label: 'Ice Cream', icon: IceCreamBowl }
  ]

  return (
    <UICheckboxGroup
      ref={ref}
      options={options || defaultFoodOptions}
      layout="vertical"
      {...props}
    />
  )
})

// checkbox-09 패턴: Icon Checkbox (Heart, Star, Bookmark)
// shadcn-ui-blocks 패턴에 맞게 순수 아이콘만 표시, 박스 없이 구현
export const UICheckboxIcon = React.forwardRef<
  HTMLButtonElement,
  {
    icon?: LucideIcon
    checkedIcon?: LucideIcon
    colorTheme?: 'heart' | 'star' | 'bookmark' | 'primary' | 'accent'
    checked?: boolean
    onCheckedChange?: (checked: boolean) => void
    size?: 'sm' | 'default' | 'lg'
    className?: string
    'aria-label'?: string
  } & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'type' | 'role' | 'aria-checked' | 'className'>
>(({
  icon,
  checkedIcon,
  colorTheme = 'heart',
  checked = false,
  onCheckedChange,
  size = 'default',
  className,
  'aria-label': ariaUILabel,
  ...buttonProps
}, ref) => {
  const variants = sizeVariants[size]
  const theme = iconThemes[colorTheme]

  const defaultIcons = {
    heart: Heart,
    star: Star,
    bookmark: BookmarkIcon
  }

  // Determine icon with fallback for themes without default icons
  const IconComponent = icon || (colorTheme in defaultIcons ? defaultIcons[colorTheme as keyof typeof defaultIcons] : Heart)
  const CheckedIcon = checkedIcon || IconComponent

  // 크기별 패딩 설정 (클릭 영역 확보용)
  const paddingClasses = {
    sm: "p-1",      // 4px padding around icon
    default: "p-1.5", // 6px padding around icon
    lg: "p-2"       // 8px padding around icon
  }

  // Generate accessible label
  const getAriaUILabel = (): string => {
    if (ariaUILabel) return ariaUILabel

    const iconName = IconComponent.displayName || IconComponent.name ||
                    (colorTheme === 'heart' ? 'heart' :
                     colorTheme === 'star' ? 'star' :
                     colorTheme === 'bookmark' ? 'bookmark' : 'icon')

    return `Toggle ${iconName}`
  }

  // Filter out component-specific props to prevent DOM pollution
  const {
    icon: _icon,
    checkedIcon: _checkedIcon,
    colorTheme: _colorTheme,
    checked: _checked,
    onCheckedChange: _onCheckedChange,
    size: _size,
    variant: _variant, // Filter out any variant prop that might come from stories
    ...domSafeProps
  } = buttonProps as any

  return (
    <button
      ref={ref}
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={getAriaUILabel()}
      onClick={() => onCheckedChange?.(!checked)}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault()
          onCheckedChange?.(!checked)
        }
      }}
      className={cn(
        // 기본 레이아웃: 아이콘 중심, 박스 없음
        "group inline-flex items-center justify-center rounded-sm transition-[transform,translate,scale,box-shadow] duration-200",
        "cursor-pointer hover:scale-110 active:scale-95",
        // 포커스: 아이콘 주변에만 링
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1",
        // 크기: 아이콘 + 패딩만, 고정 박스 크기 없음
        paddingClasses[size],
        // 비활성화 상태
        domSafeProps.disabled && "opacity-50 cursor-not-allowed hover:scale-100 active:scale-100",
        className
      )}
      data-state={checked ? "checked" : "unchecked"}
      {...domSafeProps}
    >
      {/* Unchecked state icon - 일반 outline 아이콘 */}
      <span className={cn(
        "group-data-[state=checked]:hidden transition-colors duration-200",
        theme.unchecked
      )}>
        <IconComponent className={cn(variants.icon)} />
      </span>

      {/* Checked state icon - filled 버전 또는 동일 아이콘 + fill 스타일 */}
      <span className={cn(
        "group-data-[state=unchecked]:hidden",
        theme.checked
      )}>
        <CheckedIcon className={cn(variants.icon)} />
      </span>
    </button>
  )
})

UICheckbox.displayName = "UICheckbox"
UICheckboxGroup.displayName = "UICheckboxGroup"
UICheckboxIndeterminate.displayName = "UICheckboxIndeterminate"
UICheckboxHorizontalGroup.displayName = "UICheckboxHorizontalGroup"
UICheckboxVerticalGroup.displayName = "UICheckboxVerticalGroup"
UICheckboxIcon.displayName = "UICheckboxIcon"